import { NOECOMM_CC } from '../constants/cookies'
import { SEARCH_DISPLAY_PATHNAME } from '../constants/pages'
import { Cookies } from './cookies'
import { Url } from './url'
import { WcsAkamaiCookies } from './wcs/types/akamai-cookies'

export class NoEcommerce {
  private static readonly LATAM = '/latam'
  private static readonly ROW = '/row'
  private static readonly ASEAN = '/asean'
  private static readonly MEA = '/mea'
  private static readonly TURKEY = '/turkey'

  private static readonly NO_ECOMMERCE_URLS = [
    this.LATAM,
    this.ROW,
    this.ASEAN,
    this.MEA,
    this.TURKEY,
  ]

  static getNoEcommerceCountry = (): string | null => {
    const countryCodeFromCookies = Cookies.get(NOECOMM_CC)
    const countryCodeFromLocalStorage = localStorage.getItem(NOECOMM_CC)
    if (!countryCodeFromCookies || countryCodeFromCookies === 'undefined') {
      return null
    }
    return countryCodeFromCookies || countryCodeFromLocalStorage
  }

  static setNoEcommerceCookie = (noEcommCookie: string): void => {
    Cookies.set(NOECOMM_CC, noEcommCookie)
    localStorage.setItem(NOECOMM_CC, noEcommCookie)
  }

  static removeNoEcommerceCookie = (): void => {
    Cookies.remove(NOECOMM_CC)
    localStorage.removeItem(NOECOMM_CC)
  }

  static isStoreInNoEcommerceCountries = (storeCountryCode: string): boolean => {
    return WcsAkamaiCookies.NO_ECOMMERCE_COUNTRY_CODES.includes(storeCountryCode)
  }

  static isStoreInNoEcommerceCountriesWithPrices = (storeCountryCode: string): boolean => {
    return WcsAkamaiCookies.NO_ECOMMERCE_WITH_PRICES_COUNTRY_CODES.includes(storeCountryCode)
  }

  static isStoreNoEcommerce = (): boolean => {
    const storeCountry = this.getNoEcommerceCountry()
    return storeCountry ? this.isStoreInNoEcommerceCountries(storeCountry) : false
  }

  static isStoreNoEcommerceWithPrices = (): boolean => {
    const storeCountry = this.getNoEcommerceCountry()
    if (!storeCountry) {
      return false
    }
    return (
      this.isStoreInNoEcommerceCountries(storeCountry) &&
      this.isStoreInNoEcommerceCountriesWithPrices(storeCountry)
    )
  }

  static isLatamCountry = (): boolean => {
    return this.isStoreNoEcommerce() && this.isCountryInCluster(WcsAkamaiCookies.LATIN_AMERICA)
  }

  static isRowCountry = (): boolean => {
    return this.isStoreNoEcommerce() && this.isCountryInCluster(WcsAkamaiCookies.REST_OF_WORLD)
  }

  static isAseanCountry = (): boolean => {
    return (
      this.isStoreNoEcommerce() && this.isCountryInCluster(WcsAkamaiCookies.SOUTHEAST_ASIAN_NATIONS)
    )
  }

  static isSeeCountry = (): boolean => {
    return this.isStoreNoEcommerce() && this.isCountryInCluster(WcsAkamaiCookies.SOUTHEAST_EUROPE)
  }

  static isMeaCountry = (): boolean => {
    return this.isStoreNoEcommerce() && this.isCountryInCluster(WcsAkamaiCookies.MIDDLE_EAST)
  }

  static isInternationalCountry = (): boolean => {
    return this.isStoreNoEcommerce() && this.isCountryInCluster(WcsAkamaiCookies.INTERNATIONAL)
  }

  private static isCountryInCluster = (cluster: string[]) => {
    const noEcommerceCountry = this.getNoEcommerceCountry()
    return noEcommerceCountry ? cluster.includes(noEcommerceCountry) : false
  }

  static isTurkey = (): boolean => {
    return this.getNoEcommerceCountry() === WcsAkamaiCookies.TURKEY
  }

  static isAnyNoEcommerceInUrl = (): boolean => {
    return this.NO_ECOMMERCE_URLS.some(
      (country) =>
        Url.isCountryInUrl(country) || window.location.pathname === SEARCH_DISPLAY_PATHNAME
    )
  }

  static checkAndCleanNoEcommerceCookie = (): void => {
    if (!!this.getNoEcommerceCountry() && !this.isAnyNoEcommerceInUrl()) {
      this.removeNoEcommerceCookie()
    }
  }

  static checkAndAddNoEcommerceCookie = (): void => {
    if (this.isAnyNoEcommerceInUrl()) {
      if (
        Url.isCountryInUrl(this.ASEAN) &&
        (!this.getNoEcommerceCountry() || !this.isAseanCountry())
      ) {
        this.setNoEcommerceCookie(WcsAkamaiCookies.ASEAN_CC)
      }

      if (Url.isCountryInUrl(this.ROW) && (!this.getNoEcommerceCountry() || !this.isRowCountry())) {
        this.setNoEcommerceCookie(WcsAkamaiCookies.ROW_CC)
      }

      if (
        Url.isCountryInUrl(this.LATAM) &&
        (!this.getNoEcommerceCountry() || !this.isLatamCountry())
      ) {
        this.setNoEcommerceCookie(WcsAkamaiCookies.LATAM_CC)
      }

      if (Url.isCountryInUrl(this.MEA) && (!this.getNoEcommerceCountry() || !this.isMeaCountry())) {
        this.setNoEcommerceCookie(WcsAkamaiCookies.MEA_CC)
      }
    }
  }
}
